<template>
  <iRow vertical-align="middle" wrap="nowrap" @click="handleEditorFocus">
    <iEditor
      ref="createMessageEditor"
      :upload-post-image="uploadMessageImage"
      focus
      name="message"
      placeholder="Type a message..."
      variant="slack"
      :segments="segments"
      :preview-link-data="previewLinkData"
      :at-symbol-items="filteredAtSymbolItems"
      :loading-at-symbol-items="loadingAtSymbolItems"
      @handle-link-modal-open="$event.openModal(true)"
      @search:at-symbol="handleSearchMention($event.text, myMentionsContext)"
      @select:at-symbol="handleSelectMention"
      @input="draftMessage = $event"
      @submit="sendNewMessage"
      @get:link-preview="handleGetPreviewData"
      @focus="handleEditorFocus"
      @remove-image="removeImageFromPendingState($event)"
    />
  </iRow>
</template>

<script>
import { mapActions, mapState, mapWritableState } from "pinia";
import { useConversationStore } from "%/apps/conversations/stores/conversation-store";
import { useMessageStore } from "%/apps/conversations/stores/message-store";
import { useChannelStore } from "~/stores/channel-store";

export default {
  name: "CreateMessage",
  props: {
    context: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
    parentContext: {
      type: String,
      required: false,
      default: "",
    },
    parentEntityId: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ["editor:focus", "message-sent"],
  data() {
    return {
      previewLinkData: null,
    };
  },
  computed: {
    ...mapState(useChannelStore, ["currentChannel", "activeEntityId"]),
    ...mapWritableState(useMessageStore, [
      "atSymbolItems",
      "filteredAtSymbolItems",
      "loadingAtSymbolItems",
    ]),
    ...mapWritableState(useConversationStore, ["draftMessages"]),
    myMentionsContext() {
      return this.parentContext || this.context;
    },
    myMentionsEntityId() {
      return this.parentEntityId || this.entityId;
    },
    segments() {
      const message = this.draftMessages[this.context || this.parentContext]?.[this.entityId || this.parentEntityId];
      if (message) {
        return JSON.parse(message);
      }
      return [];
    },
    draftMessage: {
      get() {
        return this.draftMessages[this.context || this.parentContext]?.[this.entityId || this.parentEntityId] || "";
      },
      set(newMessage) {
        const context = this.context || this.parentContext;
        const entityId = this.entityId || this.parentEntityId;
        this.draftMessages = {
          ...this.draftMessages,
          [context]: {
            ...this.draftMessages[context],
            [entityId]: newMessage,
          },
        };
      },
    },
  },
  watch: {
    activeEntityId(newValue) {
      if (newValue) {
        this.focusEditor();
      }
    },
  },
  mounted() {
    this.focusEditor();
  },
  methods: {
    ...mapActions(useConversationStore, [
      "sendMessage",
      "isSendingMessage",
    ]),
    ...mapActions(useMessageStore, [
      "getLinkPreviewData",
      "uploadMessageImage",
      "removeImageFromPendingState",
      "replyMessage",
      "handleSearchMention",
      "handleSelectMention",
    ]),
    focusEditor() {
      this.$nextTick(() => {
        const ref = this.$refs.createMessageEditor;
        if (ref && ref.editorInstance?.commands) {
          ref.editorInstance?.commands?.focus();
          ref.editorInstance?.commands?.placeCursorAtEnd();
        }
      });
    },
    sendNewMessage() {
      if (!this.draftMessage) {
        return;
      }

      if (this.context === "reply") {
        this.replyMessage(this.entityId, this.draftMessage);
        this.draftMessage = "";
        this.$emit("message-sent");
        return;
      }

      this.sendMessage(this.draftMessage, this.context, this.entityId);
      this.draftMessage = "";
      this.$emit("message-sent");
    },
    handleGetPreviewData(url) {
      if (!url) {
        this.previewLinkData = null;
        return;
      }

      this.getLinkPreviewData(url, data => {
        if (this.draftMessage.includes(url)) {
          this.previewLinkData = data;
        }
      });
    },
    handleEditorFocus() {
      this.$emit("editor:focus");
    },
  },
};
</script>

<style lang="scss" scoped>
.send-btn {
  position: absolute;
  right: 12px;
  top: 0;
  bottom: 0;
  margin-block: auto;
}

.message-box-container {
  position: relative;
}

.message-box-container :deep(input) {
  padding-right: 36px;
}
</style>

